/**
 * landing-page.tsx — public marketing surface (iltizam Phase 6).
 *
 * Hero with the wireframe logo, a single ink CTA + outline trial CTA,
 * and a 3-card feature bento underneath. Warm-cream gradient runs the
 * full page via `bg-iltizam-app`.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, Network, ShieldCheck, ArrowUpRight } from 'lucide-react';
import { LanguageToggle } from '@/components/language-toggle';

export function LandingPage(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="min-h-dvh bg-iltizam-app">
      <header className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-6 py-5">
        <Link to="/" className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-iltizam-ink"
          >
            <span className="h-4 w-4 rounded-[5px] bg-iltizam-yellow" />
          </span>
          <span className="text-[21px] font-extrabold tracking-[0.5px] text-iltizam-ink">
            {t('brand.name')}
          </span>
        </Link>
        <div className="flex items-center gap-3">
          <LanguageToggle />
          <Link
            to="/auth/login"
            className="inline-flex items-center gap-1.5 rounded-pill bg-iltizam-ink px-4 py-2 text-[12.5px] font-bold text-white transition-transform hover:-translate-y-0.5"
          >
            {t('landing.cta_login')}
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </header>

      <section className="mx-auto max-w-5xl px-6 py-20 text-center">
        <p className="mb-3 text-[11px] font-bold uppercase tracking-[3px] text-iltizam-muted">
          {t('brand.tagline')}
        </p>
        <h1 className="mb-6 text-[44px] font-extrabold leading-[1.05] tracking-[-1.5px] text-iltizam-ink sm:text-[60px]">
          {t('landing.heading')}
        </h1>
        <p className="mx-auto mb-10 max-w-2xl text-[15.5px] leading-relaxed text-iltizam-muted">
          {t('landing.subheading')}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/auth/login"
            className="inline-flex items-center gap-1.5 rounded-pill bg-iltizam-ink px-6 py-3 text-[13px] font-bold text-white transition-transform hover:-translate-y-0.5"
          >
            {t('landing.cta_login')}
            <ArrowUpRight className="h-4 w-4" />
          </Link>
          <a
            href="mailto:ibrahim.alhazaa@gmail.com?subject=Imtithal%20Trial"
            className="inline-flex items-center rounded-pill border border-iltizam-line bg-white px-6 py-3 text-[13px] font-bold text-iltizam-ink hover:bg-iltizam-warm"
          >
            {t('landing.cta_request')}
          </a>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-24">
        <div className="grid gap-4 sm:grid-cols-3">
          <Feature
            icon={<Network className="h-5 w-5" />}
            title={t('landing.feature_engines_title')}
            body={t('landing.feature_engines_body')}
            accent="bg-iltizam-yellow-soft text-iltizam-yellow-deep"
          />
          <Feature
            icon={<ShieldCheck className="h-5 w-5" />}
            title={t('landing.feature_security_title')}
            body={t('landing.feature_security_body')}
            accent="bg-iltizam-green-soft text-[#2E6914]"
          />
          <Feature
            icon={<Building2 className="h-5 w-5" />}
            title={t('landing.feature_compliance_title')}
            body={t('landing.feature_compliance_body')}
            accent="bg-iltizam-ink text-white"
          />
        </div>
      </section>
    </div>
  );
}

function Feature({
  icon,
  title,
  body,
  accent,
}: {
  icon: JSX.Element;
  title: string;
  body: string;
  accent: string;
}): JSX.Element {
  return (
    <article className="iltizam-card flex flex-col gap-3 p-6 transition-transform hover:-translate-y-0.5 hover:shadow-bentoHover">
      <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${accent}`}>
        {icon}
      </span>
      <h3 className="text-[18px] font-extrabold leading-tight text-iltizam-ink">{title}</h3>
      <p className="text-[13px] leading-relaxed text-iltizam-muted">{body}</p>
    </article>
  );
}
